// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import * as partnersApi from '../api'

//Tiempo de vida del catálogo de socios en el store (30 minutos)
export const PARTNERS_CACHE_TTL = 30 * 60 * 1000

//El endpoint debe devolver una lista: cualquier otra forma dejaría la tabla con un
//`rows` roto
const asList = (data) => (Array.isArray(data) ? data : [])

//Redux Toolkit rellena `action.error.message` con este literal cuando el thunk se rechaza
//con `rejectWithValue`, que es justo el caso de una caída de red (timeout, CORS, backend
//abajo): sin respuesta HTTP no hay `error.response.data` que pasarle
const RTK_REJECTED_SENTINEL = 'Rejected'

//`error` guarda lo que dijo el backend y nada más. Un fallo sin respuesta no dice nada, así
//que se guarda null en vez del literal en inglés de Redux Toolkit: quien lo muestre pondrá
//su propio texto. Que la petición falló ya lo dice `status`
const readError = (action) => {
    const message = action.error?.message

    return action.payload ?? (message === RTK_REJECTED_SENTINEL ? null : message) ?? null
}

const isLatest = (state, action) => state.requestId === action.meta.requestId

export const getPartners = createAsyncThunk('appPartners/getPartners', async (_, { rejectWithValue }) => {
    try {
        return {
            data: await partnersApi.fetchPartners(),
            fetchedAt: Date.now()
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
}, {
    //Evita peticiones duplicadas y reutiliza el caché mientras siga vigente
    condition: (_, { getState }) => {
        const partners = getState()?.partners

        if (!partners) return true
        //Invalidación explícita (enableRequest): pasa aunque haya una petición en vuelo,
        //que pudo salir antes de la escritura; su respuesta se descarta por requestId
        if (partners.isRequested) return true
        //Ya hay una petición en vuelo: los demás se cuelgan de esa
        if (partners.status === 'pending') return false
        //Store vacío
        if (!partners.data?.length) return true

        return Date.now() - (partners.lastFetch ?? 0) > PARTNERS_CACHE_TTL
    }
})

export const appPartnersSlice = createSlice({
    name: 'appPartners',
    initialState: {
        data: [],
        isRequested: true,
        // status: idle | pending | fulfilled | rejected
        status: 'idle',
        error: null,
        lastFetch: null,
        requestId: null
    },
    reducers: {
        enableRequest: (state) => {
            state.isRequested = true
        }
    },
    extraReducers: builder => {
        builder.addCase(getPartners.pending, (state, action) => {
            state.status = 'pending'
            state.error = null
            //Se consume aquí para que una invalidación durante la petición siga vigente
            state.isRequested = false
            state.requestId = action.meta.requestId
        })
        builder.addCase(getPartners.fulfilled, (state, action) => {
            if (!isLatest(state, action)) return
            state.status = 'fulfilled'
            state.data = asList(action.payload.data)
            state.lastFetch = action.payload.fetchedAt
            state.error = null
        })
        builder.addCase(getPartners.rejected, (state, action) => {
            if (!isLatest(state, action)) return
            state.status = 'rejected'
            state.error = readError(action)
            //Permite reintentar en la siguiente llamada sin esperar al TTL
            state.isRequested = true
        })
    }
})

export const { enableRequest } = appPartnersSlice.actions

//Las escrituras invalidan el caché antes de recargar: sin esto la `condition` de
//getPartners cortaría la relectura y la tabla seguiría mostrando la lista anterior
export const addPartner = createAsyncThunk('appPartners/addPartner', async (partner, { rejectWithValue, dispatch }) => {
    try {
        const response = await partnersApi.createPartner(partner)
        dispatch(enableRequest())
        await dispatch(getPartners())
        return response
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const updatePartner = createAsyncThunk('appPartners/updatePartner', async (data, { rejectWithValue, dispatch }) => {
    try {
        const response = await partnersApi.updatePartner(data.id, data.partner)
        dispatch(enableRequest())
        await dispatch(getPartners())
        return response
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export default appPartnersSlice.reducer
